import { periodLabel } from '../../utility/event-period';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterModule } from '@angular/router';
import { ConnectionService } from '../../service/connection.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EventService } from '../../service/event.service';
import { TourService } from '../../service/tour.service';
import { SwalService } from '../../service/swal.service';
import { EventsMapComponent } from './events-map.component';
import { SubscriptionService } from '../../service/subscription.service';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';
import { isQuote, needsDate } from '../../utility/quote';
import { compareCreatedDesc, compareEventDate } from '../../utility/event-sort';

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.css'],
  imports: [CommonModule, FormsModule, RouterModule, RouterLink, TranslatePipe, EventsMapComponent, IconComponent, LoadErrorComponent],
})
export class EventsComponent implements OnInit {
  searchText = '';
  viewMode: 'list' | 'map' = 'list';
  filterType: 'all' | 'upcoming' | 'past' = 'upcoming';
  selectedYear: number | null = null;
  onlyQuotes = false;
  sortBy: 'date' | 'created' = 'date';
  events: any[] = [];

  // Su mobile i filtri (tipo/anno/vista) sono chiusi di default: la lista si vede subito,
  // il pulsante "Filtri" li apre a comparsa. Su desktop restano sempre visibili (CSS).
  filtersOpen = false;

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.filterType !== 'upcoming') count++;
    if (this.selectedYear) count++;
    if (this.onlyQuotes) count++;
    if (this.sortBy === 'created') count++;
    return count;
  }

  // Suggerimenti live
  suggestions: string[] = [];
  showSuggestions = false;
  collaborations: any[] = [];

  constructor(
    private router: Router,
    private connection: ConnectionService,
    private ms: EventService,
    private translate: TranslateService,
    private tour: TourService,
    private swal: SwalService,
    private subscription: SubscriptionService,
    private route: ActivatedRoute,
  ) {}

  needsDate(evento: any): boolean {
    return needsDate(evento);
  }

  isQuote(evento: any): boolean {
    return isQuote(evento);
  }

  get quoteCount(): number {
    return this.events.filter((m: any) => isQuote(m)).length;
  }

  ngOnInit() {
    this.onlyQuotes = this.route.snapshot.queryParamMap.get('stato') === 'preventivo';
    this.tour.eventi();
    this.subscription.loadEntitlements();
    this.load();
  }

  // Eventi non arrivati: errore con Riprova, non un falso "nessun evento"
  loadError = false;

  load() {
    this.loadError = false;
    this.ms.getMatrimoni(
      (data: any[]) => {
        this.events = data;
        this.loadCollaborations();
      },
      () => (this.loadError = true),
    );
  }

  /** Vista mappa: funzione Pro. Se non consentita, mostra il prompt upgrade. */
  showMapView() {
    if (!this.subscription.requirePro('La vista mappa è disponibile con il piano Pro o Agency.')) {
      return;
    }
    this.viewMode = 'map';
  }

  get canProMaps(): boolean {
    return this.subscription.canPro();
  }
  get allWeddings(): any[] {
    return [...this.otherWeddings, ...this.collaborations].sort(this.compare);
  }

  // Anno della data precisa, altrimenti quello del periodo indicativo
  private eventYear(m: any): number {
    return m.event?.date
      ? new Date(m.event.date).getFullYear()
      : +m.event?.date_hint?.year || NaN;
  }

  private get compare(): (a: any, b: any) => number {
    return this.sortBy === 'created' ? compareCreatedDesc : compareEventDate;
  }

  // Data precisa, altrimenti il periodo indicativo del preventivo
  dateLabel(m: any): string {
    if (m.event?.date) return new Date(m.event.date).toLocaleDateString(this.locale);
    return periodLabel(m.event?.date_hint, (k) => this.translate.instant(k)) || this.translate.instant('crea.period.no_date');
  }

  private get locale(): string {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'it';
    const map: Record<string, string> = { it: 'it-IT', en: 'en-GB', es: 'es-ES' };
    return map[lang] ?? 'it-IT';
  }

  get groupedWeddings(): { label: string; items: any[] }[] {
    if (this.sortBy === 'created') return [{ label: '', items: this.allWeddings }];
    const map = new Map<string, any[]>();
    for (const m of this.allWeddings) {
      const d = new Date(m.event.date);
      const key = m.event.date
        ? d.toLocaleDateString(this.locale, { month: 'long', year: 'numeric' })
        : this.translate.instant('crea.period.no_date');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(m);
    }
    return Array.from(map.entries()).map(([label, items]) => ({
      label,
      items,
    }));
  }

  loadCollaborations() {
    this.ms.getCollaborations((data: any[]) => {
      this.collaborations = data.map((w: any) => ({
        ...w,
        is_collaboration: true,
        client: {
          name: w.bride_name?.split(' ')[0] ?? '',
          lastname: w.bride_name?.split(' ')[1] ?? '',
        },
        event: { date: w.date, location: w.location, time: w.time },
      }));
    }, () => (this.loadError = true));
  }

  // ── ANNI ──────────────────────────────────────────────────────────────────
  get availableYears(): number[] {
    const years = this.events
      .map((m: any) => this.eventYear(m))
      .filter((y) => !isNaN(y));
    return [...new Set(years)].sort((a, b) => b - a);
  }

  toggleYear(year: number) {
    this.selectedYear = this.selectedYear === year ? null : year;
  }

  // ── RICERCA SMART ─────────────────────────────────────────────────────────
  updateSuggestions() {
    const q = this.searchText.trim().toLowerCase();
    if (q.length < 2) {
      this.suggestions = [];
      return;
    }
    const words = q.split(/\s+/);
    this.suggestions = this.events
      .map((m: any) => `${m.client?.name ?? ''} ${m.client?.lastname ?? ''}`.trim())
      .filter((name) => words.every((w) => name.toLowerCase().includes(w)))
      .filter((v, i, a) => a.indexOf(v) === i) // dedup
      .slice(0, 5);
  }

  selectSuggestion(name: string) {
    this.searchText = name;
    this.suggestions = [];
    this.showSuggestions = false;
    const matches = this.filteredEvents;
    if (matches.length === 1) this.router.navigate(['/evento', matches[0]._id]);
  }

  // ── FILTRO ────────────────────────────────────────────────────────────────
  get filteredEvents(): any[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const q = this.searchText.trim().toLowerCase();
    const words = q.split(/\s+/).filter(Boolean);

    return this.events
      .filter((m: any) => {
        const eventDate = new Date(m.event.date);
        const hasDate = !!m.event.date;

        const haystack = [m.client?.name, m.client?.lastname, m.event.location].filter(Boolean).join(' ').toLowerCase();
        const nameMatch = words.every((w) => haystack.includes(w));

        // Cercando si guarda su tutti gli eventi: i filtri non restringono la ricerca
        if (q) return nameMatch;

        const typeMatch =
          this.filterType === 'all' ||
          (this.filterType === 'upcoming' && (!hasDate || eventDate >= today)) ||
          (this.filterType === 'past' && hasDate && eventDate < today);

        const yearMatch =
          !this.selectedYear || this.eventYear(m) === this.selectedYear;

        const statusMatch = !this.onlyQuotes || isQuote(m);

        return nameMatch && typeMatch && yearMatch && statusMatch;
      })
      .sort(this.compare);
  }

  // ── NEXT / OTHER ──────────────────────────────────────────────────────────
  get nextWedding(): any {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Ordinando per inserimento non c'è un "prossimo" in evidenza: conta l'ordine della lista
    if (this.sortBy === 'created') return null;
    // In evidenza va un evento confermato, salvo quando si guardano solo i preventivi
    return this.filteredEvents
      .filter((m: any) => new Date(m.event.date) >= today && (this.onlyQuotes || !isQuote(m)))
      .sort(compareEventDate)[0];
  }

  get otherWeddings(): any[] {
    if (!this.nextWedding) return this.filteredEvents;
    return this.filteredEvents.filter(
      (m: any) => m._id !== this.nextWedding._id,
    );
  }

  // ── UTILS ─────────────────────────────────────────────────────────────────
  daysUntil(date: string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = date.split('T')[0].split('-').map(Number);
    const target = new Date(year, month - 1, day);
    return Math.ceil(
      (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
  }

  getServices(evento: any): string[] {
    const selectedSvcs: any[] = (evento.event?.services ?? []).filter(
      (s: any) => s.selected,
    );
    if (selectedSvcs.length > 0) {
      return selectedSvcs.slice(0, 3).map((s: any) => s.name);
    }
    const tags: string[] = [];
    const services = evento.event?.service ?? [];
    if (
      services.some(
        (s: any) => s.checked && s.service?.toLowerCase().includes('trucco'),
      )
    )
      tags.push('Trucco');
    if (
      services.some(
        (s: any) => s.checked && s.service?.toLowerCase().includes('capelli'),
      )
    )
      tags.push('Capelli');
    if (tags.length === 0)
      services
        .filter((s: any) => s.checked)
        .slice(0, 2)
        .forEach((s: any) => tags.push(s.service));
    return tags;
  }

  errorHandler(error: any) {
    this.swal.errorDialog(
      this.translate.instant('swal.error'),
      error.error?.message || this.translate.instant('swal.events_load_error'),
    );
  }

  openCreateForm() {
    this.router.navigate(['eventi/nuovo']);
  }
}
